import tokenService from './services/tokenService';
import React, { useEffect, useState } from 'react';

const PaymentSuccessPage = ({ onBackToLibrary }) => {
  const [paymentDetails, setPaymentDetails] = useState(null);
  
  useEffect(() => {
    // Get payment details from URL parameters if available
    const urlParams = new URLSearchParams(window.location.search);
    const orderID = urlParams.get('token');
    const payerID = urlParams.get('PayerID');
    
    if (orderID && payerID) {
      setPaymentDetails({ orderID, payerID });
    }
  }, []);

  return (
    <div className="min-h-screen p-6 flex items-center justify-center" style={{background: 'linear-gradient(135deg, #4A5D6B 0%, #6B8A9A 100%)'}}>
      <div className="max-w-md w-full rounded-xl p-8 text-center" style={{background: '#2C3E50', border: '1px solid rgba(212, 175, 55, 0.2)'}}>
        {/* Success Icon */}
        <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center" style={{background: 'linear-gradient(135deg, #10b981 0%, #065f46 100%)'}}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="white" strokeWidth="2" fill="none"/>
          </svg>
        </div>

        <h1 className="text-2xl font-bold text-white mb-4" style={{fontFamily: 'Georgia, serif', color: '#D4AF37'}}>
          Payment Successful!
        </h1>
        
        <p className="text-slate-300 mb-6" style={{fontFamily: 'Georgia, serif'}}>
          Thank you for subscribing to Sonosmyth! Your subscription has been activated and you now have access to all premium features.
        </p>

        {paymentDetails && (
          <div className="bg-slate-700 rounded-lg p-4 mb-6">
            <h3 className="text-white font-semibold mb-2" style={{fontFamily: 'Georgia, serif'}}>Payment Details</h3>
            <p className="text-slate-300 text-sm" style={{fontFamily: 'Georgia, serif'}}>
              Order ID: {paymentDetails.orderID}
            </p>
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={onBackToLibrary}
            className="w-full py-3 rounded-lg font-semibold transition text-white"
            style={{
              background: 'linear-gradient(135deg, #D4AF37 0%, #E6C35C 100%)',
              fontFamily: 'Georgia, serif',
              border: 'none'
            }}
          >
            Start Listening
          </button>
          
          <button
            onClick={() => window.location.href = '/payment/history'}
            className="w-full py-2 text-slate-300 text-sm transition"
            style={{fontFamily: 'Georgia, serif', background: 'none', border: 'none'}}
            onMouseEnter={(e) => e.target.style.color = '#D4AF37'}
            onMouseLeave={(e) => e.target.style.color = '#cbd5e1'}
          >
            View Payment History
          </button>
        </div>
      </div>
    </div>
  );
};

const PaymentCancelPage = ({ onBackToPricing }) => {
  return (
    <div className="min-h-screen p-6 flex items-center justify-center" style={{background: 'linear-gradient(135deg, #4A5D6B 0%, #6B8A9A 100%)'}}>
      <div className="max-w-md w-full rounded-xl p-8 text-center" style={{background: '#2C3E50', border: '1px solid rgba(239, 68, 68, 0.2)'}}>
        {/* Cancel Icon */}
        <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center" style={{background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'}}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>

        <h1 className="text-2xl font-bold text-white mb-4" style={{fontFamily: 'Georgia, serif'}}>
          Payment Cancelled
        </h1>
        
        <p className="text-slate-300 mb-6" style={{fontFamily: 'Georgia, serif'}}>
          Your payment was cancelled. No charges have been made to your account. You can try again whenever you're ready.
        </p>

        <div className="space-y-3">
          <button
            onClick={onBackToPricing}
            className="w-full py-3 rounded-lg font-semibold transition text-white"
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              fontFamily: 'Georgia, serif',
              border: 'none'
            }}
          >
            Choose a Plan
          </button>
          
          <button
            onClick={() => window.location.href = '/'}
            className="w-full py-2 text-slate-300 text-sm transition"
            style={{fontFamily: 'Georgia, serif', background: 'none', border: 'none'}}
            onMouseEnter={(e) => e.target.style.color = '#D4AF37'}
            onMouseLeave={(e) => e.target.style.color = '#cbd5e1'}
          >
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

const PaymentHistoryPage = ({ onBackToLibrary }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPaymentHistory();
  }, []);

  const fetchPaymentHistory = async () => {
    try {
      // Phase 2 cookie auth: don't gate on localStorage (always null).
      // The httpOnly cookie is sent automatically by the global fetch
      // interceptor. If the user truly isn't signed in, the backend will
      // return 401 below and we show the same error.
      const authToken = tokenService.getToken() || '';

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/history`, {
        headers: {}
      });

      if (response.status === 401 || response.status === 403) {
        setError('Please sign in to view payment history');
        setLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch payment history');
      }

      const data = await response.json();
      if (data.success) {
        setPayments(data.payments);
      } else {
        throw new Error(data.error || 'Failed to load payment history');
      }
    } catch (error) {
      console.error('Payment history error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatStatus = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'completed': return '#10b981';
      case 'created': return '#f59e0b';
      case 'denied': return '#ef4444';
      default: return '#6b7280';
    }
  };

  return (
    <div className="min-h-screen p-6" style={{background: 'linear-gradient(135deg, #4A5D6B 0%, #6B8A9A 100%)'}}>
      <nav className="flex justify-between items-center mb-8">
        <button 
          onClick={onBackToLibrary}
          className="text-white transition"
          style={{fontFamily: 'Georgia, serif', background: 'none', border: 'none'}}
          onMouseEnter={(e) => e.target.style.color = '#D4AF37'}
          onMouseLeave={(e) => e.target.style.color = 'white'}
        >
          ← Back to Library
        </button>
        <h1 className="text-2xl font-bold text-white" style={{fontFamily: 'Georgia, serif', color: '#D4AF37'}}>Payment History</h1>
        <div></div>
      </nav>

      <div className="max-w-4xl mx-auto">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-white text-lg" style={{fontFamily: 'Georgia, serif'}}>Loading payment history...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-400 text-lg" style={{fontFamily: 'Georgia, serif'}}>{error}</p>
          </div>
        ) : payments.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-300 text-lg" style={{fontFamily: 'Georgia, serif'}}>No payment history found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {payments.map((payment, index) => (
              <div key={index} className="rounded-xl p-6" style={{background: '#2C3E50', border: '1px solid rgba(212, 175, 55, 0.2)'}}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-white text-lg font-semibold mb-2" style={{fontFamily: 'Georgia, serif'}}>
                      {payment.tier_purchased ? `${payment.tier_purchased.charAt(0).toUpperCase() + payment.tier_purchased.slice(1)} Subscription` : 'Subscription Payment'}
                    </h3>
                    <p className="text-slate-300" style={{fontFamily: 'Georgia, serif'}}>
                      {formatDate(payment.created_at)}
                    </p>
                    <p className="text-slate-400 text-sm" style={{fontFamily: 'Georgia, serif'}}>
                      Order ID: {payment.paypal_order_id}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-white text-xl font-bold mb-2" style={{fontFamily: 'Georgia, serif'}}>
                      ${payment.amount.toFixed(2)}
                    </p>
                    <span 
                      className="px-3 py-1 rounded-full text-sm font-semibold text-white"
                      style={{
                        background: getStatusColor(payment.status),
                        fontFamily: 'Georgia, serif'
                      }}
                    >
                      {formatStatus(payment.status)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export { PaymentSuccessPage, PaymentCancelPage, PaymentHistoryPage };